import { useEffect, useMemo, useState } from "react"
import type { SkuCostConfig } from "@/features/profit-calculators/calculator-shared/types"

type Sku = { sku: string; productName: string }
type CostField = keyof Omit<SkuCostConfig, "sku" | "productName">

const createCosts = (skus: Sku[], initialCosts: Record<string, SkuCostConfig>) =>
  Object.fromEntries(
    skus.map(item => [
      item.sku,
      initialCosts[item.sku] ?? {
        ...item,
        productPrice: 0,
        productGst: 18,
        packingPrice: 0,
        packingGst: 18,
        otherPrice: 0
      }
    ])
  )

export const calculateSkuCost = (cost: SkuCostConfig): number =>
  cost.productPrice * (1 + cost.productGst / 100) + cost.packingPrice * (1 + cost.packingGst / 100) + cost.otherPrice

export const useSkuCostEditor = (open: boolean, uniqueSkus: Sku[], initialCosts: Record<string, SkuCostConfig>) => {
  const [costs, setCosts] = useState<Record<string, SkuCostConfig>>({})
  const [search, setSearch] = useState("")
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [bulkProductPrice, setBulkProductPrice] = useState("")
  const [bulkProductGst, setBulkProductGst] = useState("")
  const [bulkPackingPrice, setBulkPackingPrice] = useState("")
  const [bulkPackingGst, setBulkPackingGst] = useState("")
  const [bulkOtherPrice, setBulkOtherPrice] = useState("")

  useEffect(() => {
    if (!open) return
    setCosts(createCosts(uniqueSkus, initialCosts))
    setSelectedSkus(new Set())
    setPage(1)
  }, [open, initialCosts, uniqueSkus])

  const filteredSkus = useMemo(() => {
    const query = search.toLowerCase()
    return uniqueSkus.filter(
      item => item.sku.toLowerCase().includes(query) || item.productName.toLowerCase().includes(query)
    )
  }, [search, uniqueSkus])

  const paginatedSkus = useMemo(() => {
    const start = (page - 1) * rowsPerPage
    return filteredSkus.slice(start, start + rowsPerPage)
  }, [filteredSkus, page, rowsPerPage])

  const updateCost = (sku: string, field: CostField, value: number) => {
    setCosts(current => ({
      ...current,
      [sku]: { ...current[sku], [field]: value }
    }))
  }

  const toggleSku = (sku: string) => {
    setSelectedSkus(current => {
      const next = new Set(current)
      next.has(sku) ? next.delete(sku) : next.add(sku)
      return next
    })
  }

  const selectAll = (checked: boolean) =>
    setSelectedSkus(checked ? new Set(filteredSkus.map(item => item.sku)) : new Set())

  const applyBulkEdit = () => {
    if (!selectedSkus.size) return
    const fields: Array<[CostField, string]> = [
      ["productPrice", bulkProductPrice],
      ["productGst", bulkProductGst],
      ["packingPrice", bulkPackingPrice],
      ["packingGst", bulkPackingGst],
      ["otherPrice", bulkOtherPrice]
    ]

    setCosts(current => {
      const next = { ...current }
      selectedSkus.forEach(sku => {
        const item = next[sku]
        if (!item) return
        next[sku] = fields.reduce(
          (updated, [field, value]) => (value === "" ? updated : { ...updated, [field]: parseFloat(value) || 0 }),
          { ...item }
        )
      })
      return next
    })

    setBulkProductPrice("")
    setBulkProductGst("")
    setBulkPackingPrice("")
    setBulkPackingGst("")
    setBulkOtherPrice("")
    setSelectedSkus(new Set())
  }

  return {
    costs,
    search,
    setSearch,
    selectedSkus,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    bulkProductPrice,
    setBulkProductPrice,
    bulkProductGst,
    setBulkProductGst,
    bulkPackingPrice,
    setBulkPackingPrice,
    bulkPackingGst,
    setBulkPackingGst,
    bulkOtherPrice,
    setBulkOtherPrice,
    filteredSkus,
    paginatedSkus,
    totalPages: Math.ceil(filteredSkus.length / rowsPerPage),
    updateCost,
    toggleSku,
    selectAll,
    applyBulkEdit
  }
}
